import { EmailLike, PersonLike, PhoneLike, ResidentLike, UserLike } from '../models/person/personLike.data';
import { ApartmentData } from '../models/units/apartment.data';
import { UnitLocation } from '../models/units/unit.data';
import { Nullish } from '../types/typeish.type';
import { ArrHlp } from './array.helper';
import { ObjHlp } from './object.helper';
import { StrHlp } from './string.helper';

/**
 * Various functions converting objects to strings
 */
export class ToString {
  /**
   * replaces x by placeholder if !!x and x !== 0
   * @param {any}    x
   * @param {string} placeholder
   * @return {string}
   */
  public static or(x: any, placeholder: string): string {
    return ObjHlp.haveValue(x) ? x.toString() : placeholder;
  }

  /**
   * Extracts user's name in human-readable form
   * @param {PersonLike | Nullish} obj
   * @return {string} user's full name
   */
  public static fullName(obj: PersonLike | Nullish): string {
    if (obj) {
      if (StrHlp.isString(obj.name)) {
        return StrHlp.isString(obj.lastName) ? `${obj.name} ${obj.lastName}` : (obj.name as string);
      } else {
        return StrHlp.isString(obj.lastName) ? (obj.lastName as string) : '';
      }
    }
    return '';
  }

  /**
   * Extracts user's name and email in human-readable form
   * @param {UserLike | Nullish} obj
   * @return {string} user's full name
   */
  public static fullNameEmail(obj: UserLike | Nullish): string {
    if (obj) {
      const fullName = ToString.fullName(obj);
      const email = ToString.getEmail(obj);
      return email ? `${fullName} (${email})` : fullName;
    }
    return '';
  }

  // eslint-disable-next-line valid-jsdoc
  /**
   * Extracts user's name and in human-readable form
   * @param {(PersonLike & PhoneLike) | Nullish} obj
   * @return {string} user's full name
   */
  public static fullNamePhone(obj: (PersonLike & PhoneLike) | Nullish): string {
    if (obj) {
      const fullName = ToString.fullName(obj);
      const phone = ToString.getPhone(obj);
      return phone ? `${fullName} (${phone})` : fullName;
    }
    return '';
  }

  /**
   * Extracts user's name, phone number and email in human-readable form
   * @param {ResidentLike | Nullish} obj
   * @return {string} user's full name
   */
  public static fullNameContact(obj: ResidentLike | Nullish): string {
    if (obj) {
      const fullName = ToString.fullName(obj);
      const phone = ToString.getPhone(obj);
      const email = ToString.getEmail(obj);

      return `${fullName} ${email ? email + (phone ? ', ' + phone : '') : phone}`;
    }
    return '';
  }

  /**
   * Extract unit block number and number in human-readable form
   * @param {UnitLocation | Nullish} unit        unit data
   * @param {string}                 placeholder default empty
   * @return {string} unit human-readable
   */
  public static shortNumber(unit: UnitLocation | Nullish, placeholder = ''): string {
    if (unit) {
      const number = ToString.or(unit.number, placeholder);
      return ObjHlp.haveValue(unit.blockNumber) ? `${unit.blockNumber} / ${number}` : number;
    }
    return '';
  }

  /**
   * Extract unit street, block number and number in human-readable form
   * @param {UnitLocation | Nullish} unit        unit data
   * @param {string}                 placeholder default empty
   * @return {string} unit human-readable
   */
  public static longNumber(unit: UnitLocation | Nullish, placeholder = ''): string {
    const street = unit?.street;
    const number = ToString.shortNumber(unit, placeholder);
    return (street ? street + ' ' : '') + number;
  }

  /**
   * Extract unit street, block number, staircase and number in human-readable form
   * @param {UnitLocation | Nullish} loc         unit data
   * @param {string}                 placeholder replaces any missing number
   * @param {boolean}                showStreet
   * @return {string} unit human-readable
   */
  public static fullNumber(loc: UnitLocation | Nullish, placeholder = '-', showStreet = true): string {
    const p = placeholder;
    const street = loc?.street;
    const number = `${loc?.blockNumber || p} / ${(loc as ApartmentData)?.staircaseNumber || p} / ${loc?.number || p}`;
    return showStreet && street ? `${street} ${number}` : number;
  }

  /**
   * Extracts user's phone number
   * @param {any}    user
   * @param {number} index index of number, minus indexes iterates from end to start, default: 0
   * @return {string} user's phone number if phone is string index is ignored if index out of bounds returns last element for
   *                  plus index, and first - for minus index
   */
  public static getPhone(user: PhoneLike | Nullish, index = 0): string {
    if (user?.phone) {
      if (StrHlp.isString(user.phone)) {
        return user.phone as string;
      }
      if (Array.isArray(user.phone)) {
        const arr = user.phone as string[];
        if (!arr.length) return '';
        return ArrHlp.get(arr, index) ?? '';
      }
    }
    return '';
  }

  /**
   * Extracts user's email address
   * @param {any}    user
   * @param {number} index index of address, minus indexes iterates from end to start, default: 0
   * @return {string} user's email address if email address is string index is ignored if index out of bounds returns last
   *                  element for plus index, and first - for minus index
   */
  public static getEmail(user: EmailLike | Nullish, index = 0): string {
    if (user?.email) {
      if (StrHlp.isString(user.email)) {
        return user.email as string;
      }
      if (Array.isArray(user.email)) {
        const arr = user.email as string[];
        if (!arr.length) return '';
        return ArrHlp.get(arr, index) ?? '';
      }
    }
    return '';
  }
}
